import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Building2, Search, PoundSterling, FileText, ChevronRight, Users,
  ArrowLeft
} from "lucide-react";
import { API } from "@/App";

const SolicitorFirmsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [firms, setFirms] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchFirms();
  }, []);

  const fetchFirms = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/solicitors/firms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFirms(response.data.firms || []);
    } catch (err) {
      toast.error("Failed to load solicitor firms");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const filteredFirms = firms.filter(firm => 
    firm.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/sales/progression')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sales
              </Button>
              <div className="h-8 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-800">Solicitor Firms</h1>
                  <p className="text-sm text-slate-500">{firms.length} firms working with us</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search solicitor firms..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-firms"
            />
          </div>
        </div>

        {/* Firms Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFirms.map((firm) => (
            <Card 
              key={firm.firm_id} 
              className="hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate(`/sales/solicitor/${encodeURIComponent(firm.firm_id)}`)}
              data-testid={`firm-card-${firm.firm_id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-600 transition-colors">
                    <Building2 className="w-6 h-6 text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-600 transition-colors" />
                </div>
                
                <h3 className="font-semibold text-lg text-slate-800 mb-1 group-hover:text-indigo-600 transition-colors">
                  {firm.name}
                </h3>
                
                {firm.contact_email && (
                  <p className="text-sm text-slate-500 mb-4 truncate">{firm.contact_email}</p>
                )}
                
                <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span className="text-sm">
                      <span className="font-semibold text-slate-800">{firm.case_count}</span>
                      <span className="text-slate-500"> files</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PoundSterling className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-semibold text-slate-800">
                      {formatCurrency(firm.pipeline_value)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredFirms.length === 0 && (
          <Card className="mt-8">
            <CardContent className="p-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No Firms Found</h3>
              <p className="text-slate-500">
                {searchQuery ? `No firms matching "${searchQuery}"` : "No solicitor firms in the system"}
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SolicitorFirmsPage;
